<template>
  <div>
    <div
      class="w-full h-full relative"
      style="user-select: none"
      @mousedown="editorClick"
      @touchstart="editorClick"
    >
      <editor
        ref="clickPane"
        :content="downstreamContent"
        class="full"
        style="clip-path: url(#clipPath); overflow: hidden"
        @loaded="handleLoadEvent"
        @loading="handleLoadingEvent"
      >
      </editor>
      <svg
        ref="svgPane"
        class="full"
        :style="{ overflow: 'visible', pointerEvents: 'none' }"
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
      >
        <path
          :d="clipPath"
          fill="none"
          stroke="#606f7b"
          stroke-dasharray="3"
          stroke-width="1"
          vector-effect="non-scaling-stroke"
        ></path>
        <clipPath id="clipPath" clipPathUnits="objectBoundingBox">
          <path :d="clipPath"></path>
        </clipPath>
      </svg>
      <editor-item-controls
        v-for="element of downstreamContent"
        :key="element.uid"
        :elements="[element]"
        :active="
          1 === chosenElements.length && chosenElements.includes(element.uid)
        "
        :ratio="ratio"
        @update="update"
        @click="select"
        @delete="deleteItem"
        @edit="editItem"
        @unselect="unselectAllElements"
        @add="addItem"
      >
      </editor-item-controls>
      <editor-item-controls
        v-if="chosenElements.length > 1"
        :elements="chosenElementsReal"
        :active="true"
        :ratio="ratio"
        @update="update"
        @click="select"
        @delete="deleteItem"
        @edit="editItem"
        @unselect="unselectAllElements"
      >
      </editor-item-controls>
      <div class="full" :class="{ grid: showGrid }"></div>
    </div>
  </div>
</template>

<script>
import EditorItemControls from './EditorItemControls';
import {
  TrackingMixin,
  elementTypeToTrackingName
} from '@/mixins/gtm-tracking';
import { useControlLayerStore } from '@/store-pinia/control-layer';
import { mapActions, mapState } from 'pinia';
import { useCompositionStore } from '@/store-pinia/composition';
import {
  DEFAULT_RECTANGLE_CLIP_PATH,
  ELEMENT_TYPE_UPLOAD
} from '@/plugins/composition-renderer/constants';
import { useCustomerUploadsStore } from '@/store-pinia/customerUploads';
import { useCompositionWarningStore } from '@/store-pinia/composition-warnings';
import { useClipboardStore } from '@/store-pinia/clipboard';
import { useLoadingIndicatorStore } from '../../store-pinia/loading-indicator';

export default {
  name: 'EditorControlLayer',
  components: { EditorItemControls },
  mixins: [TrackingMixin],
  props: {
    content: {
      type: [Object, Array],
      required: true
    },
    ratio: {
      type: Number,
      required: true
    },
    clipPath: {
      type: String,
      required: false,
      default: () => DEFAULT_RECTANGLE_CLIP_PATH
    },
    showGrid: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    chosenElementsReal() {
      return this.downstreamContent.filter(element =>
        this.chosenElements.includes(element.uid)
      );
    },
    downstreamContent() {
      return this.content;

      // if (0 === this.editingElements.length) {
      //   return this.content;
      // }
      // const contentWithoutSelected = this.content.filter(
      //   element => !this.editingElements.has(element.uid)
      // );
      //
      // this.editingElements.forEach(element => contentWithoutSelected.push(element));
      //
      // return contentWithoutSelected;
    },
    ...mapState(useControlLayerStore, {
      chosenElements: 'selectedElementUids'
    })
  },
  mounted() {
    const store = useControlLayerStore();
    store.recalculateWatchers();
    window.addEventListener('keydown', this.keyListener);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.keyListener);
  },
  methods: {
    ...mapActions(useControlLayerStore, {
      select: 'selectElement',
      unselectAllElements: 'unselectAllElements',
      update: 'updateElements'
    }),
    ...mapActions(useCompositionWarningStore, ['removeWarning']),
    editorClick($event) {
      if (this.$refs.clickPane.$el === $event.target) {
        this.unselectAllElements();
      }
    },
    deleteItem(uid) {
      const compositionStore = useCompositionStore();
      this.removeWarning(uid);
      this.trackEvent({
        action:
          elementTypeToTrackingName(
            compositionStore.getElementByUid(uid).type
          ) + '_Delete'
      });
      this.removeItem(uid);
    },
    removeItem(uid) {
      const compositionStore = useCompositionStore();
      const element = compositionStore.getElementByUid(uid);
      if (null === element) {
        return;
      }
      const elementSide = compositionStore.getPrintSideNameOfElement(uid);
      compositionStore.removeElement(uid);

      this.$toasted.info(this.$t('warning.undoDeleteText'), {
        duration: 10000,
        className: 'bg-sh-primary',
        action: [
          {
            text: this.$t('warning.undoDeleteAction'),
            onClick: (e, toastObject) => {
              // Add a copy of the element back to the Composition
              compositionStore.addElement(elementSide, {
                ...element,
                uid: window.crypto.randomUUID()
              });
              toastObject.goAway(0);
            }
          }
        ]
      });

      this.unselectAllElements();
    },
    addItem(itemConfig) {
      const uid = window.crypto.randomUUID();
      const item = {
        ...itemConfig,
        uid
      };
      useCompositionStore().addElement(this.$store.state.activePrintSide, item);
      this.unselectAllElements();
      // Make sure the new element is selected
      this.select(uid);
      useClipboardStore().copy(item);
    },
    editItem(uid) {
      const compositionStore = useCompositionStore();
      this.trackEvent({
        action: `${elementTypeToTrackingName(
          compositionStore.getElementByUid(uid).type
        )}_Edit`
      });
      useControlLayerStore().editElement(uid);
    },
    handleLoadEvent({ element, content }) {
      useLoadingIndicatorStore().finishLoading(element.uid);

      // Set height of element after image has loaded.
      if (
        !useControlLayerStore().elementAllowedToResizeByImageDimensions.has(
          element.uid
        )
      ) {
        useControlLayerStore().elementAllowedToResizeByDPI.delete(element.uid);
        return;
      }
      useControlLayerStore().elementAllowedToResizeByImageDimensions.delete(
        element.uid
      );

      const { naturalWidth, naturalHeight } = content;
      const { heightInInch, widthInInch } =
        this.$store.state.printplates[this.$store.state.activePrintSide];
      const ratio = widthInInch / heightInInch;

      const heightPercent =
        (element.width / naturalWidth) * naturalHeight * ratio;
      let changeset = {
        height: heightPercent
      };

      if (
        ELEMENT_TYPE_UPLOAD === element.type &&
        useControlLayerStore().elementAllowedToResizeByDPI.has(element.uid)
      ) {
        useControlLayerStore().elementAllowedToResizeByDPI.delete(element.uid);
        const metaData = useCustomerUploadsStore().getMetaData(element.srcFile);

        if (null !== metaData) {
          const minDpi = useControlLayerStore().configData.uploadMinDpi;
          const hundredPercentMaximum = heightInInch * minDpi;

          let maximumHeightPercent =
            (metaData.height / hundredPercentMaximum) * 100;

          if (maximumHeightPercent < heightPercent) {
            const factor = maximumHeightPercent / heightPercent;
            changeset = {
              height: heightPercent * factor,
              width: element.width * factor
            };
          }
        }
      }

      this.update([
        {
          uid: element.uid,
          changeset: changeset
        }
      ]);
      // End of set height
    },
    handleLoadingEvent({ element }) {
      useLoadingIndicatorStore().startLoading(element.uid);
      // Load meta data for uploads while the upload file is loading
      if (ELEMENT_TYPE_UPLOAD === element.type) {
        useCustomerUploadsStore().loadMetaData(element.srcFile);
      }
    },
    isTextInputFocused() {
      const activeElement = document.activeElement;
      const tagName = activeElement.tagName.toLowerCase();
      return (
        ['input', 'textarea', 'select'].includes(tagName) ||
        activeElement.isContentEditable
      );
    },
    keyListener(ev) {
      if (this.isTextInputFocused()) {
        return;
      }

      switch (ev.key.toLowerCase()) {
        case 'v':
          if (!ev.ctrlKey && !ev.metaKey) return;
          ev.preventDefault();
          useClipboardStore()
            .paste()
            .forEach(element => {
              const itemSite = useCompositionStore().getPrintSideNameOfElement(
                element.uid
              );
              const isSiteChanged =
                itemSite !== this.$store.state.activePrintSide;

              this.addItem({
                ...element,
                left: isSiteChanged ? 50 - element.width / 2 : element.left + 4,
                top: isSiteChanged ? 50 - element.height / 2 : element.top + 4
              });
            });
          break;
      }
    }
  }
};
</script>

<style scoped>
.grid {
  --grid-color: #858ea9;
  --grid-width: 1px;
  @apply opacity-25 z-0;
  background-image: linear-gradient(
      to right,
      var(--grid-color) var(--grid-width),
      transparent var(--grid-width)
    ),
    linear-gradient(
      to bottom,
      var(--grid-color) var(--grid-width),
      transparent var(--grid-width)
    );
  background-size: 10% 10%;
  background-position: top left;
  mix-blend-mode: exclusion;
}
.full {
  @apply absolute w-full h-full;
  top: 0;
  left: 0;
}
</style>
